import { Trans, useLingui } from "@lingui/react/macro";
import { useState } from "react";
import { z } from "zod";
import { ActionCardButton } from "@/components/ActionCard/ActionCard";
import { DialogPanel } from "@medoma/components/DialogPanel/DialogPanel";
import { Loading } from "@medoma/components/Loading/Loading";
import PlusIcon from "@medoma/components/icons/PlusIcon";
import { AddActivityForm } from "@/forms/AddActivity/AddActivityForm";
import { TemplatesList } from "@/forms/AddActivity/TemplatesList";
import { Tabs, TabList, TabPanel, Tab } from "@medoma/components/Tabs";
import { useCurrentEmployee } from "@/api/Employee";

const activityTabSchema = z.enum(["templates", "custom"]);
type IActivityTab = z.infer<typeof activityTabSchema>;

export const AddActivityAction = () => {
  const { t } = useLingui();
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<IActivityTab>(
    activityTabSchema.enum.templates,
  );
  const { data: currentEmployee, isPending } = useCurrentEmployee();
  // If fetching the current employee fails, we fall back to null — no competence
  // is pre-seeded and the user has to add one manually in the form.
  const currentEmployeeCompetence = currentEmployee?.competence ?? null;

  const close = () => setIsOpen(false);

  return (
    <>
      <ActionCardButton
        icon={<PlusIcon />}
        title={t`Lägg till aktivitet`}
        onClick={() => setIsOpen(true)}
      />
      <DialogPanel
        isOpen={isOpen}
        onClose={close}
        title={t`Lägg till aktivitet`}
        position="bottom"
      >
        {isPending ? (
          <Loading message={t`Hämtar uppgifter om din roll`} />
        ) : (
          <Tabs>
            <TabList stateOwner="react">
              <Tab
                isSelected={activeTab === activityTabSchema.enum.templates}
                onClick={() => setActiveTab(activityTabSchema.enum.templates)}
              >
                <Trans>Mallar</Trans>
              </Tab>
              <Tab
                isSelected={activeTab === activityTabSchema.enum.custom}
                onClick={() => setActiveTab(activityTabSchema.enum.custom)}
              >
                <Trans>Anpassad</Trans>
              </Tab>
            </TabList>
            <TabPanel>
              {activeTab === activityTabSchema.enum.templates ? (
                <TemplatesList
                  onSuccess={close}
                  currentEmployeeCompetence={currentEmployeeCompetence}
                />
              ) : (
                <AddActivityForm
                  onSuccess={close}
                  currentEmployeeCompetence={currentEmployeeCompetence}
                />
              )}
            </TabPanel>
          </Tabs>
        )}
      </DialogPanel>
    </>
  );
};
